import { useCallback, useEffect, useState } from "react";
import { api, STATUS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { FileText, Download, FileSpreadsheet, User, MapPin, TrendingUp, Loader2, PieChart } from "lucide-react";
import { toast } from "sonner";
import { isAdminLike } from "@/lib/roles";
import { StatusTotalCards } from "@/components/StatusTotalCards";

// Relatórios com totalizações agregadas + comparativo período anterior (fev/2026):
// - 4 cards por status via componente StatusTotalCards (com delta vs período anterior)
// - Tabela de Desempenho por Entregador (total + breakdown de status + taxa conclusão)
// - Resumo Geográfico (Regiões + Bairros por volume)
// - 2 grupos de export: por-linha (PDF/XLSX/CSV) E totalizadores agregados (PDF/XLSX)
// Foco em VOLUME (não há taxa de frete).

export default function Relatorios() {
  const { user, empresas, currentEmpresaId } = useAuth();
  const isAdmin = isAdminLike(user);
  const [entregadores, setEnt] = useState([]);
  const [regioes, setReg] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loadingTotals, setLoadingTotals] = useState(false);
  const [f, setF] = useState({
    empresa_id: currentEmpresaId || "current",
    entregador_id: "all",
    regiao_id: "all",
    status: "all",
    start: "",
    end: "",
  });

  useEffect(() => {
    // Cadastros globais — só recarregar no mount (entregadores/regiões são raramente alterados
    // via UI enquanto o usuário está em Relatórios). Filtros dependem só destes valores locais.
    api.get("/entregadores").then((r) => setEnt(r.data));
    api.get("/regioes").then((r) => setReg(r.data));
  }, []);

  const buildQs = useCallback(() => {
    const p = new URLSearchParams();
    if (f.entregador_id !== "all") p.set("entregador_id", f.entregador_id);
    if (f.regiao_id !== "all") p.set("regiao_id", f.regiao_id);
    if (f.status !== "all") p.set("status", f.status);
    if (f.start) p.set("start", f.start);
    if (f.end) p.set("end", f.end);
    if (isAdmin && f.empresa_id && f.empresa_id !== "current") p.set("empresa_id", f.empresa_id);
    return p.toString();
  }, [f, isAdmin]);

  const loadTotals = useCallback(async () => {
    setLoadingTotals(true);
    try {
      // compare_previous=true faz backend calcular período anterior automaticamente quando start/end estão setados
      const r = await api.get(`/reports/totals?${buildQs()}&compare_previous=true`);
      setTotals(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar totais");
    } finally { setLoadingTotals(false); }
  }, [buildQs]);

  // Recarrega totais quando filtros mudam (com debounce leve pra não spammar).
  // `loadTotals` já é useCallback com deps corretas → identidade estável.
  useEffect(() => {
    const t = setTimeout(loadTotals, 250);
    return () => clearTimeout(t);
  }, [loadTotals, currentEmpresaId]);

  const empresaNome = () => {
    if (f.empresa_id === "current") return empresas.find((e) => e.id === currentEmpresaId)?.nome || "atual";
    return empresas.find((e) => e.id === f.empresa_id)?.nome || "empresa";
  };

  const download = async (kind) => {
    try {
      // Iter 89 (fev/2026): PDF passa por async job — /reports/pdf/async
      // enfileira, polling em /reports/jobs/{id}, download quando pronto.
      // Se >1000 linhas, backend automaticamente gera CSV (BLOCO 3 iter 88).
      if (kind === "pdf") {
        return await downloadAsyncReport();
      }
      const r = await api.get(`/reports/${kind}?${buildQs()}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      const nome = (empresaNome() || "entregas").replace(/\s+/g, "_").toLowerCase();
      a.download = `relatorio_${nome}_${new Date().toISOString().slice(0,10)}.${kind}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`Relatório ${kind.toUpperCase()} baixado`);
    } catch (e) {
      console.error("Falha ao gerar relatório detalhado", kind, e);
      toast.error(e?.response?.data?.detail || "Erro ao gerar relatório");
    }
  };

  const downloadAsyncReport = async () => {
    // Enfileira o job → poll → baixa. UX: um toast por fase.
    const nome = (empresaNome() || "entregas").replace(/\s+/g, "_").toLowerCase();
    const enq = toast.loading("Enfileirando relatório…");
    let jobId = null;
    try {
      const r = await api.post(`/reports/pdf/async?${buildQs()}`);
      jobId = r.data.job_id;
      toast.dismiss(enq);
    } catch (e) {
      toast.dismiss(enq);
      toast.error(e?.response?.data?.detail || "Não foi possível enfileirar o relatório");
      return;
    }
    const pollToast = toast.loading("Gerando relatório em segundo plano…", { duration: Infinity });
    let attempts = 0;
    const maxAttempts = 120;   // 120 × 2s = 4 min
    while (attempts < maxAttempts) {
      await new Promise((res) => setTimeout(res, 2000));
      try {
        const st = await api.get(`/reports/jobs/${jobId}`);
        const s = st.data.status;
        if (s === "done") {
          toast.dismiss(pollToast);
          const dl = await api.get(`/reports/jobs/${jobId}/download`, { responseType: "blob" });
          const url = URL.createObjectURL(dl.data);
          const a = document.createElement("a"); a.href = url;
          const ext = st.data.format || "pdf";
          a.download = `relatorio_${nome}_${new Date().toISOString().slice(0, 10)}.${ext}`;
          document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
          if (ext === "csv" && st.data.row_count > 1000) {
            toast.success(`Relatório com ${st.data.row_count.toLocaleString('pt-BR')} linhas — CSV baixado (PDF acima do limite)`, { duration: 6000 });
          } else {
            toast.success(`Relatório ${ext.toUpperCase()} baixado (${st.data.row_count} entrega(s))`);
          }
          return;
        }
        if (s === "error") {
          toast.dismiss(pollToast);
          toast.error(`Job falhou: ${st.data.error || 'sem detalhe'}`);
          return;
        }
      } catch (e) {
        toast.dismiss(pollToast);
        toast.error("Erro consultando status do job");
        return;
      }
      attempts++;
    }
    toast.dismiss(pollToast);
    toast.error("Timeout aguardando o job. Ele pode ainda estar rodando — verifique em alguns minutos.");
  };

  const downloadTotais = async (kind) => {
    // Endpoints /reports/totals/{xlsx|pdf} — exportam AGREGADO (não linha por entrega)
    try {
      const r = await api.get(`/reports/totals/${kind}?${buildQs()}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      const nome = (empresaNome() || "entregas").replace(/\s+/g, "_").toLowerCase();
      a.download = `totais_${nome}_${new Date().toISOString().slice(0,10)}.${kind}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`Totais ${kind.toUpperCase()} baixados`);
    } catch (e) {
      console.error("Falha ao gerar totalizadores", kind, e);
      toast.error(e?.response?.data?.detail || "Erro ao gerar totais");
    }
  };

  const totalAll = totals?.total ?? 0;
  const pct = (n) => (totalAll > 0 ? Math.round((n / totalAll) * 100) : 0);
  const hasPeriod = !!(f.start && f.end);
  const hasComparativo = !!(totals?.previous);

  return (
    <div className="space-y-6" data-testid="page-relatorios">
      <div>
        <h1 className="text-4xl font-extrabold">Relatórios</h1>
        <p className="text-muted-foreground">Filtros por empresa · totalizações e exportação PDF / CSV / XLSX</p>
      </div>

      <Card>
        <CardHeader><CardTitle>Filtros</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-6 gap-3 items-end">
          {isAdmin && (
            <div>
              <Label>Empresa</Label>
              <Select value={f.empresa_id} onValueChange={(v) => setF({ ...f, empresa_id: v })}>
                <SelectTrigger data-testid="rel-empresa"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="current">Empresa atual (menu)</SelectItem>
                  {empresas.map((e) => (
                    <SelectItem key={e.id} value={e.id} data-testid={`rel-emp-opt-${e.id}`}>{e.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div><Label>Entregador</Label>
            <Select value={f.entregador_id} onValueChange={(v) => setF({ ...f, entregador_id: v })}>
              <SelectTrigger data-testid="rel-entregador"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem>{entregadores.map((e) => <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Região</Label>
            <Select value={f.regiao_id} onValueChange={(v) => setF({ ...f, regiao_id: v })}>
              <SelectTrigger data-testid="rel-regiao"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todas</SelectItem>{regioes.map((r) => <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="rel-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Data Início</Label>
            <Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} onClick={(e) => { try { e.target.showPicker && e.target.showPicker(); } catch (err) { console.debug("showPicker indisponível", err); } }} data-testid="rel-start"/></div>
          <div><Label>Data Fim</Label>
            <Input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} onClick={(e) => { try { e.target.showPicker && e.target.showPicker(); } catch (err) { console.debug("showPicker indisponível", err); } }} data-testid="rel-end"/></div>
        </CardContent>
      </Card>

      <div className="bg-orange-50/60 border border-orange-200 rounded-lg p-3 text-sm text-orange-900 flex items-center justify-between flex-wrap gap-2" data-testid="rel-info">
        <div><b>Relatório da empresa:</b> {empresaNome()}</div>
        <div className="text-xs text-orange-800 flex items-center gap-3 flex-wrap">
          <span><TrendingUp className="w-3.5 h-3.5 inline mr-1" />Foco em volume</span>
          {hasComparativo && (
            <span data-testid="comparativo-info">
              📊 Comparado com: <b>{totals.previous.period.start}</b> — <b>{totals.previous.period.end}</b>
            </span>
          )}
        </div>
      </div>

      {/* ---- Totalização por Status ---- */}
      <div>
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <h2 className="text-xl font-bold flex items-center gap-2">Totalização por Status</h2>
          {loadingTotals && <Loader2 className="w-4 h-4 animate-spin text-orange-600" />}
          <div className="text-sm text-muted-foreground ml-auto" data-testid="totals-total">
            Total no período: <b className="text-orange-700 text-lg">{totalAll}</b>
            {hasComparativo && (
              <span className="ml-3 text-xs" data-testid="comparativo-total">
                (anterior: <b>{totals.previous.total}</b>
                {totals.previous.total > 0 && (
                  <span className={`ml-1 font-bold ${totals.total >= totals.previous.total ? "text-emerald-700" : "text-rose-700"}`}>
                    {totals.total >= totals.previous.total ? "▲" : "▼"} {Math.abs(Math.round(((totals.total - totals.previous.total) / totals.previous.total) * 100))}%
                  </span>
                )}
                )
              </span>
            )}
          </div>
        </div>
        <StatusTotalCards totals={totals} testIdPrefix="status-card" />
        {totals && (
          <div className="text-xs text-muted-foreground mt-2 flex gap-4 flex-wrap">
            <span>Aguardando: <b>{totals.status.aguardando}</b></span>
            <span>Em Trânsito: <b>{totals.status.em_transito}</b></span>
            <span className="text-slate-500">(Pendentes = Aguardando + Em Trânsito + Remarcadas)</span>
            {!hasPeriod && (
              <span className="text-amber-700">💡 Informe Data Início + Fim para ver o comparativo com o período anterior</span>
            )}
          </div>
        )}
      </div>

      {/* ---- Desempenho por Entregador ---- */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <User className="w-5 h-5 text-orange-600" />
            Desempenho por Entregador
            {totals && <Badge variant="outline" className="ml-2 text-xs">{totals.entregadores.length}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[440px]">
            <table className="w-full text-sm" data-testid="table-entregadores">
              <thead className="bg-slate-50 border-b sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Entregador</th>
                  <th className="px-3 py-2 text-center font-semibold">Total</th>
                  <th className="px-3 py-2 text-center font-semibold text-emerald-700">Concluídas</th>
                  <th className="px-3 py-2 text-center font-semibold text-rose-700">Canceladas</th>
                  <th className="px-3 py-2 text-center font-semibold text-amber-700">Remarcadas</th>
                  <th className="px-3 py-2 text-center font-semibold text-blue-700">Pendentes</th>
                  <th className="px-3 py-2 text-center font-semibold">Taxa Conclusão</th>
                </tr>
              </thead>
              <tbody>
                {!totals && (
                  <tr><td colSpan={7} className="text-center text-muted-foreground py-6">Carregando…</td></tr>
                )}
                {totals && totals.entregadores.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-muted-foreground py-6">Nenhum entregador com entregas no período</td></tr>
                )}
                {totals?.entregadores.map((e, idx) => (
                  <tr key={e.entregador_id || "__none__"} className={`border-b hover:bg-slate-50/50 ${idx === 0 ? "bg-orange-50/30" : ""}`} data-testid={`ent-row-${e.entregador_id || "none"}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {idx === 0 && <Badge className="bg-orange-100 text-orange-800 border-orange-300 text-[10px]">🏆 Top</Badge>}
                        <b>{e.nome}</b>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center font-bold text-lg">{e.total}</td>
                    <td className="px-3 py-2 text-center text-emerald-700 font-semibold">{e.concluidas}</td>
                    <td className="px-3 py-2 text-center text-rose-700 font-semibold">{e.canceladas}</td>
                    <td className="px-3 py-2 text-center text-amber-700 font-semibold">{e.remarcadas}</td>
                    <td className="px-3 py-2 text-center text-blue-700 font-semibold">{e.pendentes}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden min-w-[60px]">
                          <div className="h-full bg-emerald-500" style={{ width: `${e.taxa_conclusao}%` }} />
                        </div>
                        <span className="text-xs font-mono font-bold w-12 text-right">{e.taxa_conclusao}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ---- Espera Aguardando Conferência (fev/2026) ---- */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg text-violet-700">
            <User className="w-5 h-5" />
            Tempo de Espera — Aguardando Conferência
            {totals && <Badge variant="outline" className="ml-2 text-xs">{(totals.espera_conferencia || []).length}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[380px]">
            <table className="w-full text-sm" data-testid="table-espera-conferencia">
              <thead className="bg-violet-50 border-b sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Entregador</th>
                  <th className="px-3 py-2 text-center font-semibold">Qtd Aguardando</th>
                  <th className="px-3 py-2 text-center font-semibold">Espera Média</th>
                  <th className="px-3 py-2 text-center font-semibold">Maior Espera</th>
                  <th className="px-3 py-2 text-center font-semibold">Menor Espera</th>
                </tr>
              </thead>
              <tbody>
                {!totals && (
                  <tr><td colSpan={5} className="text-center text-muted-foreground py-6">Carregando…</td></tr>
                )}
                {totals && (totals.espera_conferencia || []).length === 0 && (
                  <tr><td colSpan={5} className="text-center text-muted-foreground py-6" data-testid="espera-conf-empty">Nenhuma entrega aguardando conferência no período</td></tr>
                )}
                {totals?.espera_conferencia?.map((e) => {
                  const alertMax = e.espera_max_min > 15;
                  const rowCls = alertMax ? "bg-rose-50/50" : "hover:bg-violet-50/40";
                  return (
                    <tr key={e.entregador_id || "__none__"} className={`border-b ${rowCls}`} data-testid={`ent-espera-${e.entregador_id || "none"}`}>
                      <td className="px-3 py-2 font-semibold">
                        <div className="flex items-center gap-2">
                          {e.nome}
                          {alertMax && (
                            <span className="text-[10px] font-bold text-rose-700 bg-rose-100 border border-rose-300 rounded-full px-1.5 py-0.5 uppercase animate-pulse">
                              Urgente
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-violet-100 text-violet-800 text-sm font-bold">{e.qtd}</span>
                      </td>
                      <td className="px-3 py-2 text-center font-mono">{e.espera_media_min} min</td>
                      <td className={`px-3 py-2 text-center text-lg font-extrabold ${alertMax ? "text-rose-700" : "text-violet-700"}`}>{e.espera_max_min} min</td>
                      <td className="px-3 py-2 text-center font-mono">{e.espera_min_min} min</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ---- Resumo Geográfico ---- */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin className="w-5 h-5 text-teal-600" />
              Por Região
              {totals && <Badge variant="outline" className="ml-2 text-xs">{totals.regioes.length}</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-y-auto max-h-[380px]">
              <table className="w-full text-sm" data-testid="table-regioes">
                <thead className="bg-slate-50 border-b sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Região</th>
                    <th className="px-3 py-2 text-right font-semibold">Entregas</th>
                    <th className="px-3 py-2 text-left font-semibold w-32">% do total</th>
                  </tr>
                </thead>
                <tbody>
                  {totals && totals.regioes.length === 0 && (
                    <tr><td colSpan={3} className="text-center text-muted-foreground py-6">Sem dados</td></tr>
                  )}
                  {totals?.regioes.map((r) => (
                    <tr key={r.regiao_id || r.nome} className="border-b hover:bg-slate-50/50" data-testid={`reg-row-${r.regiao_id}`}>
                      <td className="px-3 py-2 font-semibold">{r.nome}</td>
                      <td className="px-3 py-2 text-right font-bold">{r.total}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-teal-500" style={{ width: `${pct(r.total)}%` }} />
                          </div>
                          <span className="text-[10px] font-mono w-8 text-right">{pct(r.total)}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin className="w-5 h-5 text-indigo-600" />
              Por Bairro
              {totals && <Badge variant="outline" className="ml-2 text-xs">{totals.bairros.length}</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-y-auto max-h-[380px]">
              <table className="w-full text-sm" data-testid="table-bairros">
                <thead className="bg-slate-50 border-b sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Bairro</th>
                    <th className="px-3 py-2 text-left font-semibold">Região</th>
                    <th className="px-3 py-2 text-right font-semibold">Entregas</th>
                  </tr>
                </thead>
                <tbody>
                  {totals && totals.bairros.length === 0 && (
                    <tr><td colSpan={3} className="text-center text-muted-foreground py-6">Sem dados</td></tr>
                  )}
                  {totals?.bairros.map((b) => (
                    <tr key={b.bairro_id || b.nome} className="border-b hover:bg-slate-50/50" data-testid={`bai-row-${b.bairro_id}`}>
                      <td className="px-3 py-2 font-semibold">{b.nome}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{b.regiao_nome}</td>
                      <td className="px-3 py-2 text-right font-bold">{b.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-3 flex-wrap pt-2 border-t">
        <div className="w-full space-y-3">
          <div>
            <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
              <PieChart className="w-4 h-4 text-indigo-600" />Exportar totalizadores (agregado)
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button data-testid="btn-totals-pdf" onClick={() => downloadTotais("pdf")} className="bg-indigo-600 hover:bg-indigo-700">
                <FileText className="w-4 h-4 mr-2" />Totais PDF
              </Button>
              <Button data-testid="btn-totals-xlsx" onClick={() => downloadTotais("xlsx")} className="bg-indigo-600 hover:bg-indigo-700">
                <FileSpreadsheet className="w-4 h-4 mr-2" />Totais XLSX
              </Button>
              <Button data-testid="btn-totals-csv" onClick={() => downloadTotais("csv")} variant="outline">
                <Download className="w-4 h-4 mr-2" />Totais CSV
              </Button>
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4 text-orange-600" />Exportar detalhado (linha por entrega)
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button data-testid="btn-pdf" onClick={() => download("pdf")} className="bg-orange-600 hover:bg-orange-700">
                <FileText className="w-4 h-4 mr-2" />Exportar PDF
              </Button>
              <Button data-testid="btn-xlsx" onClick={() => download("xlsx")} className="bg-emerald-600 hover:bg-emerald-700">
                <FileSpreadsheet className="w-4 h-4 mr-2" />Exportar XLSX
              </Button>
              <Button data-testid="btn-csv" onClick={() => download("csv")} variant="outline">
                <Download className="w-4 h-4 mr-2" />Exportar CSV
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
